import React from "react";
import { getVidyardCoverImageUrl, getVidyardURL, getVidyardUUID, addParamToURL } from "@qlik/qlik/src/lib/util/helpers";
import { VIDEO_PLAYER_LABEL } from "@qlik/qlik/src/lib/constants/media-constants";

/**
 * Function to get the image URL.
 * @param {Array} desktopMedia - The desktop media data.
 * @param {Array} mobileMedia - The mobile media data.
 * @param {Array} mediaCoverImage - The media cover image data.
 * @param {Array} mediaPlayIcon - The media play icon data.
 * @returns {Object} The URLs for the desktop image, mobile image, media cover image, and media play icon.
 */
const getImageUrl = (desktopMedia, mobileMedia, mediaCoverImage, mediaPlayIcon) => {
    // Cloudinary base URL
    const srcBaseURL = `https://res.cloudinary.com/${process.env.GATSBY_CLOUDINARY_CLOUD_NAME}/image/upload/`;

    // Desktop Image URL
    let desktopImageURL = "";

    if (desktopMedia?.length) {
        desktopImageURL = `${srcBaseURL}w_${desktopMedia[0].width}/q_auto/${desktopMedia[0].public_id}.${desktopMedia[0].format}`;
    }

    // Mobile Image URL
    let mobileImageURL = desktopMedia?.resource_type === "image" ? desktopImageURL : null;

    if (mobileMedia?.length) {
        mobileImageURL = `${srcBaseURL}w_768/q_auto/${mobileMedia[0].public_id}.${mobileMedia[0].format}`;
    }

    // Media Cover Image URL
    const mediaCoverImageURL = mediaCoverImage?.[0]
        ? `${srcBaseURL}w_${mediaCoverImage[0].width}/q_auto/${mediaCoverImage[0].public_id}.${mediaCoverImage[0].format}`
        : "";

    // Media Play Icon URL
    const mediaPlayIconURL = mediaPlayIcon?.[0]
        ? `${srcBaseURL}w_${mediaPlayIcon[0].width}/q_auto/${mediaPlayIcon[0].public_id}.${mediaPlayIcon[0].format}`
        : "";

    return { desktopImageURL, mobileImageURL, mediaCoverImageURL, mediaPlayIconURL };
};

/**
 * Function to render a Vidyard placeholder image.
 * This is necessary for the Vidyard API to render and fire events on the video.
 * It is also hidden since we have custom images and play buttons for the video.
 * @param {string} videoExternalUrl - The external URL of the video.
 * @param {string} videoClassName - Additional CSS classes for the video component.
 * @param {string} alt - The alt text for the video.
 * @returns {JSX.Element} The video player element.
 */
const renderVidyardHiddenImg = (vidyardVideoUrl) => {
    return (
        vidyardVideoUrl && (
            <div className="hidden">
                <img
                    alt="video"
                    style={{ maxWidth: "100%" }}
                    className="vidyard-player-embed"
                    src={getVidyardCoverImageUrl(vidyardVideoUrl)}
                    data-uuid={getVidyardUUID(vidyardVideoUrl)}
                    data-v="4"
                    data-type="lightbox"
                    data-autoplay="1"
                />
            </div>
        )
    );
};

/**
 * Media component
 * This component renders the media content.
 * @param {Object} media - The media data.
 * @param {string} className - Additional CSS classes for the component.
 * @param {string} videoClassName - Additional CSS classes for the video component.
 * @param {string} alt - The alt text for the image.
 * @param {string} link - The link to the image.
 * @param {string} width - The width of the image.
 * @param {string} height - The height of the image.
 */
const Media = ({
    lazyLoad = true,
    media = {
        desktopMedia: [],
        mobileMedia: [],
        mediaCoverImage: [],
        mediaPlayIcon: [],
        videoExternalUrl: "",
        mediaAltText: "",
        mediaBehavior: "",
        id: "",
        textWrap: "",
    },
    className,
    videoClassName,
    alt = "",
    link,
    width,
    height,
    inline = false,
    options = {},
}) => {
    const getLoadingAttribute = () => (lazyLoad ? "lazy" : "eager");
    const alternativeText = alt || media?.mediaAltText;
    if (!link && !media) {
        return null;
    }

    const inlineWrapperClassName = "inline-block";
    const textWrapClass =
        // eslint-disable-next-line no-nested-ternary
        media.textWrap === "Float Left"
            ? "md:float-left mr-3 mb-1.5"
            : media.textWrap === "Float Right"
            ? "md:float-right ml-3 mb-1.5"
            : "";

    if (link) {
        const imageWithLink = (
            <picture>
                <source
                    media="(max-width:768px)"
                    srcSet={
                        decodeURIComponent(link) !== link
                            ? link
                            : encodeURI(link.includes("/f_auto") ? link.replace("/f_auto", "") : link)
                    }
                />
                <img
                    loading={getLoadingAttribute()}
                    src={link.includes("/f_auto") ? link.replace("/f_auto", "") : link}
                    alt={alternativeText}
                    className={className}
                    width={width}
                    height={height}
                />
            </picture>
        );
        return inline ? (
            <span className={`${inlineWrapperClassName} ${textWrapClass}`}>{imageWithLink}</span>
        ) : (
            imageWithLink
        );
    }

    const { desktopImageURL, mobileImageURL, mediaCoverImageURL, mediaPlayIconURL } = getImageUrl(
        media.desktopMedia,
        media.mobileMedia,
        media.mediaCoverImage,
        media.mediaPlayIcon
    );

    // Check desktop media type - Image
    if (media.desktopMedia?.[0]?.resource_type === "image") {
        const imageElement = (
            <>
                <picture>
                    <source media="(max-width:768px)" srcSet={mobileImageURL && encodeURI(mobileImageURL)} />
                    <img
                        loading={getLoadingAttribute()}
                        src={desktopImageURL}
                        alt={alternativeText}
                        className={className}
                        width={width}
                        height={height}
                    />
                </picture>
                {media.mediaCaptionText?.mediaCaptionText && (
                    <span className="figcaption">{media.mediaCaptionText?.mediaCaptionText}</span>
                )}
            </>
        );
        return inline ? (
            <span className={`${inlineWrapperClassName} ${textWrapClass}`}>{imageElement}</span>
        ) : (
            imageElement
        );
    }

    const vidyardVideoUrl = getVidyardURL(media.videoExternalUrl);
    const vidyardVideoUrlWithAutoplay = addParamToURL(vidyardVideoUrl, "autoplay", "1");

    // Inline video
    if (media.mediaBehavior === "Inline") {
        const videoAttr = {
            loop: true,
            autoPlay: true,
            muted: true,
            playsInline: true,
            ...options,
        };

        // Non-vidyard video attributes (Cloudinary)
        if (media.videoExternalUrl === null) {
            const vidSources = [];

            // Loop through video sources
            if (media.desktopMedia?.length) {
                for (const mediaItem of media.desktopMedia) {
                    const srcAttrs = {
                        src: mediaItem.secure_url,
                        type: `video/${mediaItem.format}`,
                    };

                    // If the format is "mov", we need to set the type to "mp4" and remove transformations from the url
                    // Also respect the "No Transforms" option from the Media content type
                    if (mediaItem.format === "mov" || media.mediaOptions?.includes("No Transforms")) {
                        srcAttrs.type = "video/mp4";
                        srcAttrs.src = mediaItem.secure_url.replace(/\/f_auto|\/q_auto/g, "");
                    }

                    // Update the sources array with new source attributes
                    vidSources.push(srcAttrs);
                }
            }

            // Set loop based on Media field
            if (media.mediaOptions?.includes("Loop")) videoAttr.loop = true;

            return (
                <video
                    className={`focus-visible:outline-none inset-[1px] clip-path-none ${videoClassName}`}
                    {...videoAttr}
                >
                    {vidSources.map((source) => (
                        <source key={`vid-${source.src}`} {...source} />
                    ))}
                </video>
            );
        }

        return (
            <>
                {renderVidyardHiddenImg(vidyardVideoUrl)}
                <iframe
                    title={media?.mediaAltText}
                    allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    className={`${videoClassName} inline-external-video`}
                    src={vidyardVideoUrl || media?.videoExternalUrl}
                />
            </>
        );
    }
    /* Popup - render popup with following priorities
     * Render Vidyard player if vidyard video, else the provided URL or desktopMedia itself
     * show vidyard cover image if vidyard video else show the provided
     * show provided play icon if available & default if vidyard video
     */
    const vidyardCoverImgURL = getVidyardCoverImageUrl(vidyardVideoUrl);
    return (
        media.mediaBehavior === "Pop-up" && (
            <>
                {renderVidyardHiddenImg(vidyardVideoUrl)}
                <div className="relative mediaWrapper">
                    <span
                        style={{ backgroundImage: `url(${mediaPlayIconURL})` }}
                        className="play-icon"
                        href={
                            vidyardVideoUrlWithAutoplay || media.videoExternalUrl || media.desktopMedia?.[0]?.secure_url
                        }
                        data-fancybox={media?.id || ""}
                        data-type={vidyardVideoUrl ? "iframe" : undefined}
                        data-iframe={vidyardVideoUrl ? '{"preload":false}' : undefined}
                        aria-label={VIDEO_PLAYER_LABEL}
                    />
                    <picture>
                        <source
                            media="(max-width:768px)"
                            srcSet={
                                (mobileImageURL && encodeURI(mobileImageURL)) ||
                                (mediaCoverImageURL && encodeURI(mediaCoverImageURL)) ||
                                (vidyardCoverImgURL && encodeURI(vidyardCoverImgURL))
                            }
                        />
                        <img
                            src={mediaCoverImageURL || vidyardCoverImgURL}
                            alt={alternativeText}
                            loading="lazy"
                            className={className}
                        />
                    </picture>
                </div>
            </>
        )
    );
};

export default Media;
